import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box, 
  Paper, 
  Typography, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  DirectionsCar as CarIcon,
  Bookmark as BookmarkIcon
} from '@mui/icons-material';
import { 
  fetchRideTemplates,
  deleteRideTemplate
} from '../../redux/slices/ridesSlice';
import { getVehicleTypeLabel } from '../../config/vehicleTypes';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';

const TemplatesList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const { templates, isLoading, error } = useSelector(state => state.rides);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  useEffect(() => {
    dispatch(fetchRideTemplates());
  }, [dispatch]);
  
  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };
  
  const handleOpenDeleteDialog = (template) => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };
  
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setTemplateToDelete(null);
  };
  
  const handleDeleteTemplate = () => {
    if (templateToDelete) {
      dispatch(deleteRideTemplate(templateToDelete.id))
        .then((result) => {
          if (!result.error) {
            setSnackbar({
              open: true,
              message: 'Vorlage erfolgreich gelöscht',
              severity: 'success'
            });
          } else {
            setSnackbar({
              open: true,
              message: `Fehler beim Löschen der Vorlage: ${result.error.message}`,
              severity: 'error'
            });
          }
        });
      handleCloseDeleteDialog();
    }
  };
  
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  
  // Filter templates based on search term
  const filteredTemplates = templates.filter((template) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      template.customer_name?.toLowerCase().includes(searchLower) ||
      template.pickup_address?.toLowerCase().includes(searchLower) ||
      template.destination_address?.toLowerCase().includes(searchLower) ||
      template.template_name?.toLowerCase().includes(searchLower)
    );
  });
  
  // Format date for display
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unbekannt';
    
    let date;
    if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    } else if (timestamp.toDate) {
      // Firestore timestamp
      date = timestamp.toDate();
    } else {
      date = new Date(timestamp);
    }
    
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Map vehicle type to readable string
  const getVehicleType = (type) => {
    return getVehicleTypeLabel(typeof type === 'string' ? parseInt(type) : type);
  };
  
  // Map order type to readable string
  const getOrderType = (type) => {
    switch (type) {
      case '1':
      case 1:
        return 'Vorbestellung';
      case '2':
      case 2:
        return 'Sofortbestellung';
      default:
        return 'Sofortbestellung';
    }
  };
  
  if (isLoading) {
    return <Loading message="Vorlagen werden geladen..." />;
  }
  
  if (error) {
    return (
      <ErrorMessage
        title="Fehler beim Laden der Vorlagen"
        message={error}
        onRetry={() => dispatch(fetchRideTemplates())}
      />
    );
  }
  
  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <PageHeader
        title="Fahrtvorlagen"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Fahrten', path: '/fahrten' },
          { label: 'Vorlagen', path: '/vorlagen' }
        ]}
      />
      
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <TextField
          label="Suchen"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
          sx={{ width: 300 }}
        />
        
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/vorlagen/neu')}
        >
          Neue Vorlage
        </Button>
      </Box>
      
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Kunde</TableCell>
              <TableCell>Start</TableCell>
              <TableCell>Ziel</TableCell>
              <TableCell>Fahrzeugtyp</TableCell>
              <TableCell>Bestelltyp</TableCell>
              <TableCell>Erstellt am</TableCell>
              <TableCell>Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTemplates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body1" sx={{ py: 2 }}>
                    {searchTerm ? 'Keine Vorlagen gefunden für diese Suche.' : 'Keine Vorlagen vorhanden.'}
                  </Typography>
                  {searchTerm ? (
                    <Button variant="text" onClick={() => setSearchTerm('')}>
                      Suche zurücksetzen
                    </Button>
                  ) : (
                    <Button 
                      variant="contained" 
                      startIcon={<AddIcon />}
                      onClick={() => navigate('/vorlagen/neu')}
                      sx={{ mt: 1 }}
                    >
                      Erste Vorlage erstellen
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              filteredTemplates.map((template) => (
                <TableRow key={template.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <BookmarkIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="body2">
                        {template.template_name || `${template.pickup_address.split(',')[0]} → ${template.destination_address.split(',')[0]}`}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{template.customer_name || 'Nicht angegeben'}</TableCell>
                  <TableCell>{template.pickup_address?.split(',')[0] || 'Nicht angegeben'}</TableCell>
                  <TableCell>{template.destination_address?.split(',')[0] || 'Nicht angegeben'}</TableCell>
                  <TableCell>
                    <Chip
                      icon={<CarIcon />}
                      label={getVehicleType(template.vehicle_type)}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getOrderType(template.order_type)}
                      size="small"
                      variant="outlined"
                      color={template.order_type === '1' || template.order_type === 1 ? 'secondary' : 'primary'}
                    />
                  </TableCell>
                  <TableCell>{formatDate(template.created_at)}</TableCell>
                  <TableCell>
                    <Tooltip title="Bearbeiten">
                      <IconButton 
                        size="small" 
                        color="primary"
                        onClick={() => navigate(`/vorlagen/${template.id}/bearbeiten`)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Löschen">
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => handleOpenDeleteDialog(template)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Vorlage löschen
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Sind Sie sicher, dass Sie die Vorlage "{templateToDelete?.template_name || 'Diese Vorlage'}" löschen möchten?
            Diese Aktion kann nicht rückgängig gemacht werden.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            Abbrechen
          </Button>
          <Button onClick={handleDeleteTemplate} color="error" variant="contained">
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TemplatesList;